import {AlertCircle, RefreshCw} from "lucide-react";
import {useState} from "react";
import {useSearchParams} from "react-router-dom";

import {Alert, AlertDescription, AlertTitle} from "@/components/ui/alert";
import {Button} from "@/components/ui/button";
import {Skeleton} from "@/components/ui/skeleton";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "@/components/ui/tabs";
import {PageContent, PageHeader} from "@/src/modules/core/components";
import type {Property} from "@/src/modules/properties/types";

import {
  BreakdownsSection,
  DashboardFilters,
  EmailsSection,
  KpisGrid,
  ManagementSection,
  TimeSeriesChart,
} from "../components";
import useDashboardFilters from "../hooks/use-dashboard-filters";
import useDashboardOverview from "../hooks/use-dashboard-overview";

const DASHBOARD_TABS = ["overview", "operations", "email"] as const;
type DashboardTab = (typeof DASHBOARD_TABS)[number];

function parseDashboardTab(raw: string | null): DashboardTab {
  if (raw && (DASHBOARD_TABS as readonly string[]).includes(raw)) {
    return raw as DashboardTab;
  }
  return "overview";
}

export function DashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseDashboardTab(searchParams.get("tab"));

  const {filters, setPeriod, setContextPropertyIds} = useDashboardFilters();
  const [selectedProperties, setSelectedProperties] = useState<Property[]>([]);

  const {data, isPending, isFetching, isError, refetch} = useDashboardOverview(filters);

  const handlePropertiesChange = (properties: Property[]) => {
    setSelectedProperties(properties);
    setContextPropertyIds(properties.map((p) => p.id));
  };

  const handleTabChange = (value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", value);
      return next;
    });
  };

  return (
    <>
      <PageHeader>
        <PageHeader.TitleWrapper>
          <PageHeader.Title title="Dashboard" />
          <PageHeader.RefreshButton
            size="icon"
            onRefresh={() => refetch()}
            isFetching={isFetching}
          />
        </PageHeader.TitleWrapper>
      </PageHeader>

      <PageContent>
        <div className="flex flex-col gap-4">
          <DashboardFilters
            filters={filters}
            selectedProperties={selectedProperties}
            onPeriodChange={setPeriod}
            onPropertiesChange={handlePropertiesChange}
          />

          {isError ? (
            <Alert variant="destructive">
              <AlertCircle />
              <AlertTitle>Could not load dashboard</AlertTitle>
              <AlertDescription>
                We hit an error fetching the dashboard summary. You can retry, or come back
                in a moment.
                <div className="mt-3">
                  <Button size="sm" variant="outline" onClick={() => refetch()}>
                    <RefreshCw data-icon="inline-start" /> Retry
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          ) : isPending || !data ? (
            <DashboardSkeleton />
          ) : (
            <>
              <Tabs value={tab} onValueChange={handleTabChange} className="w-full">
                <div className="overflow-x-auto pb-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:overflow-visible">
                  <TabsList variant="default" className="h-auto min-h-9 w-max max-w-none flex-wrap md:flex-nowrap">
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="operations">Operations</TabsTrigger>
                    <TabsTrigger value="email">Email</TabsTrigger>
                  </TabsList>
                </div>

                <TabsContent value="overview" className="mt-4 flex flex-col gap-4">
                  <KpisGrid kpis={data.kpis} density="compact" />
                  <TimeSeriesChart
                    data={data.time_series}
                    description={`${data.period.current.from_local} → ${data.period.current.to_local} (${data.period.timezone})`}
                  />
                  <BreakdownsSection breakdowns={data.breakdowns} />
                </TabsContent>

                <TabsContent value="operations" className="mt-4 flex flex-col gap-4">
                  <p className="text-muted-foreground text-xs">
                    Shifts, officers, and property counts are always company-wide; they are not
                    narrowed by the property filter.
                  </p>
                  <ManagementSection management={data.management} />
                </TabsContent>

                <TabsContent value="email" className="mt-4 flex flex-col gap-4">
                  <p className="text-muted-foreground text-xs">
                    Email delivery metrics are always company-wide.
                  </p>
                  <EmailsSection emails={data.emails} />
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </PageContent>
    </>
  );
}

function DashboardSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <Skeleton className="h-16 w-full rounded-lg" />
      <div className="flex flex-wrap gap-2">
        {Array.from({length: 3}).map((_, i) => (
          <Skeleton key={i} className="h-9 w-24 rounded-lg" />
        ))}
      </div>
      <div className="w-full">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({length: 8}).map((_, i) => (
            <Skeleton key={i} className="h-[8.5rem] rounded-xl" />
          ))}
        </div>
      </div>
      <Skeleton className="h-[410px] w-full rounded-xl" />
    </div>
  );
}
